import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Helper: currency formatting
const fmt = (n) => {
  if (n == null || isNaN(n)) return 'N/A';
  const rounded = Math.round(n);
  return '$' + rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Deterministic color palette
const palette = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728',
  '#9467bd', '#8c564b', '#e377c2', '#7f7f7f',
  '#bcbd22', '#17becf', '#4c78a8', '#f58518',
  '#54a24b', '#e45756', '#72b7b2', '#b279a2'
];
const colorFor = (key) => {
  // simple hash
  let h = 0;
  const s = String(key || '');
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  const idx = Math.abs(h) % palette.length;
  return palette[idx];
};

// Build union of date labels
const collectLabels = (seriesArr) => {
  const set = new Set();
  for (const s of seriesArr || []) {
    for (const pt of s.series || []) {
      set.add(pt.date);
    }
  }
  return Array.from(set).sort();
};

const seriesToMap = (series) => {
  const m = {};
  for (const pt of (series || [])) {
    m[pt.date] = pt.value;
  }
  return m;
};

const sumSeriesByGroup = (items, groupKey) => {
  // items: [{ id, asset_type_1, asset_type_2, label, series:[{date,value}] }]
  const labels = collectLabels(items);
  const groups = {};
  for (const it of items) {
    const key = it[groupKey] || 'Unknown';
    if (!groups[key]) groups[key] = {};
    const m = seriesToMap(it.series);
    for (const d of labels) {
      const v = (m[d] === undefined || m[d] === null) ? null : Number(m[d]);
      if (v !== null && !isNaN(v)) {
        groups[key][d] = (groups[key][d] || 0) + v;
      } else {
        // keep as undefined to allow null/holes which chart can gap over
        if (!(d in groups[key])) groups[key][d] = undefined;
      }
    }
  }
  const out = [];
  for (const [k, dm] of Object.entries(groups)) {
    out.push({ key: k, data: labels.map(d => (dm[d] === undefined ? null : dm[d])) });
  }
  return { labels, datasets: out };
};

const sumAll = (items) => {
  const labels = collectLabels(items);
  const totals = labels.map(() => 0);
  for (const it of items) {
    const m = seriesToMap(it.series);
    labels.forEach((d, i) => {
      const v = (m[d] === undefined || m[d] === null) ? null : Number(m[d]);
      if (v !== null && !isNaN(v)) {
        totals[i] = (totals[i] === null ? 0 : totals[i]) + v;
      } else {
        // if currently zero and truly no contributors, mark as null to allow trimming/gaps
        if (totals[i] === 0) totals[i] = null;
      }
    });
  }
  return { labels, data: totals };
};

// Trim leading positions where all datasets are null (to avoid 0->positive visual jump)
const trimLeadingNulls = (labels, datasetsOrArray) => {
  let n = labels.length;
  if (n === 0) return { labels, datasets: datasetsOrArray };
  const isArray = Array.isArray(datasetsOrArray) && !Array.isArray(datasetsOrArray[0]);
  let firstIdx = 0;
  if (isArray) {
    const data = datasetsOrArray;
    while (firstIdx < n && (data[firstIdx] == null)) firstIdx++;
    return { labels: labels.slice(firstIdx), datasets: data.slice(firstIdx) };
  }
  const dsets = datasetsOrArray;
  // find first index where any dataset has a non-null and > 0 value
  const hasPositiveAt = (i) => dsets.some(ds => ds.data && ds.data[i] != null && Number(ds.data[i]) > 0);
  while (firstIdx < n && !hasPositiveAt(firstIdx)) firstIdx++;
  return {
    labels: labels.slice(firstIdx),
    datasets: dsets.map(ds => ({ ...ds, data: ds.data.slice(firstIdx) }))
  };
};

const buildDatasets = (mode, items) => {
  if (!items || items.length === 0) return { labels: [], datasets: [] };
  if (mode === 'aggregate') {
    const agg = sumAll(items);
    const trimmed = trimLeadingNulls(agg.labels, agg.data);
    return {
      labels: trimmed.labels,
      datasets: [{
        label: 'All Assets',
        data: trimmed.datasets,
        borderColor: colorFor('all'),
        backgroundColor: colorFor('all'),
        tension: 0.1,
        pointRadius: 0,
        spanGaps: true
      }]
    };
  }
  if (mode === 'type1') {
    const res = sumSeriesByGroup(items, 'asset_type_1');
    const trimmed = trimLeadingNulls(res.labels, res.datasets.map(s => ({ label: s.key, data: s.data })));
    return {
      labels: trimmed.labels,
      datasets: trimmed.datasets.map(s => ({
        label: s.label,
        data: s.data,
        borderColor: colorFor(s.label),
        backgroundColor: colorFor(s.label),
        tension: 0.1,
        pointRadius: 0,
        spanGaps: true
      }))
    };
  }
  if (mode === 'type2') {
    const res = sumSeriesByGroup(items, 'asset_type_2');
    const trimmed = trimLeadingNulls(res.labels, res.datasets.map(s => ({ label: s.key, data: s.data })));
    return {
      labels: trimmed.labels,
      datasets: trimmed.datasets.map(s => ({
        label: s.label,
        data: s.data,
        borderColor: colorFor(s.label),
        backgroundColor: colorFor(s.label),
        tension: 0.1,
        pointRadius: 0,
        spanGaps: true
      }))
    };
  }
  // perAsset
  const labels = collectLabels(items);
  const datasets = items.map(it => ({
    label: it.label || it.description || String(it.id),
    data: labels.map(d => {
      const v = (seriesToMap(it.series))[d];
      return (v === undefined ? null : v);
    }),
    borderColor: colorFor(it.id),
    backgroundColor: colorFor(it.id),
    tension: 0.1,
    pointRadius: 0,
    spanGaps: true
  }));
  return { labels, datasets };
};

const AssetValueChart = ({ series = [], defaultMode = 'aggregate' }) => {
  const [mode, setMode] = useState(defaultMode);

  const data = useMemo(() => buildDatasets(mode, series), [mode, series]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false, axis: 'x' },
    animation: { duration: 0 },
    plugins: {
      legend: {
        position: 'top',
        labels: { color: '#e5e7eb' }
      },
      title: {
        display: true,
        text: 'Assets Value Over Time',
        color: '#e5e7eb'
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y)}`
        }
      }
    },
    scales: {
      x: {
        ticks: { color: '#d1d5db' }
      },
      y: {
        ticks: {
          color: '#d1d5db',
          callback: (val) => fmt(val)
        }
      }
    }
  }), [mode]);

  const Toggle = () => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
      {[
        { key: 'aggregate', label: 'Aggregate' },
        { key: 'type1', label: 'By Type 1' },
        { key: 'type2', label: 'By Type 2' },
        { key: 'perAsset', label: 'Per-Asset' }
      ].map(btn => (
        <button
          key={btn.key}
          onClick={() => setMode(btn.key)}
          className={'btn btn-sm ' + (mode === btn.key ? 'btn-primary' : 'btn-outline-secondary')}
        >
          {btn.label}
        </button>
      ))}
    </div>
  );

  return (
    <div style={{ width: '100%', height: 480, marginBottom: 64 }}>
      <Toggle />
      <Line options={options} data={{ labels: data.labels, datasets: data.datasets }} />
    </div>
  );
};

export default AssetValueChart;
